import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  formatMoney,
  HOURS_SOURCE_LABELS,
  type JobHoursEntry,
  type JobHoursRequest,
  type JobHoursResponse,
  jobHoursSchema,
  type JobPersonChoice,
  type JobResourcesRequest,
  type JobResponse,
  jobResourcesSchema,
} from '@k-management/shared';
import { useEffect, useRef, useState } from 'react';
import { ConfirmDialog } from '../../components/ConfirmDialog.js';
import { Field } from '../../components/Field.js';
import { apiRequest } from '../../lib/api.js';
import { formatDate } from '../../lib/format.js';
import { type FieldErrors, showServerError, validate } from '../../lib/form-validation.js';
import { jobKeys } from './jobs-ui.js';

const hoursKey = (id: string) => [...jobKeys.all, 'hours', id] as const;

/** «30,5». */
function formatHours(value: string): string {
  return Number(value).toLocaleString('it-IT', { maximumFractionDigits: 2 });
}

/**
 * Persone e ore (2.42): who works on a commessa or a progetto with the hours
 * planned, and the hours worked, by hand until K-Presenze. The €/ora and the
 * costs only with «Costi delle persone».
 */
export function PeopleHours({ job }: { job: JobResponse }) {
  const queryClient = useQueryClient();
  const [planning, setPlanning] = useState(false);
  const [adding, setAdding] = useState(false);
  const [removing, setRemoving] = useState<JobHoursEntry | null>(null);
  const hours = useQuery({
    queryKey: hoursKey(job.id),
    queryFn: () => apiRequest<JobHoursResponse>(`/jobs/${job.id}/hours`),
  });
  // «Aggiorna»: the hours of K-Presenze read again now (2.43).
  const sync = useMutation({
    mutationFn: () =>
      apiRequest<JobHoursResponse>(`/jobs/${job.id}/hours/sync`, { method: 'POST' }),
    onSuccess: (fresh) => queryClient.setQueryData(hoursKey(job.id), fresh),
  });
  const remove = useMutation({
    mutationFn: (entry: JobHoursEntry) =>
      apiRequest<JobHoursResponse>(`/jobs/${job.id}/hours/${entry.id}`, { method: 'DELETE' }),
    onSuccess: async () => {
      setRemoving(null);
      await queryClient.invalidateQueries({ queryKey: hoursKey(job.id) });
    },
  });
  if (hours.isError) return <p className="error">{hours.error.message}</p>;
  if (!hours.data) return <p className="muted">Caricamento…</p>;
  const data = hours.data;
  const editable = job.you.edit && !data.readOnly;
  const costs = data.costsVisible;
  const money = (value: string | null) => (value === null ? '—' : formatMoney(value));
  const noun = job.kind === 'progetto' ? 'progetto' : 'commessa';
  const link = data.kPresenze;

  return (
    <section className="stack">
      <p className="muted small">
        {link.connected ? (
          <>
            K-Presenze:{' '}
            {link.syncedAt
              ? `ore lette il ${new Date(link.syncedAt).toLocaleString('it-IT', { dateStyle: 'short', timeStyle: 'short' })}`
              : 'ore non ancora lette'}{' '}
            ·{' '}
            <button
              type="button"
              className="link"
              disabled={sync.isPending}
              onClick={() => sync.mutate()}
            >
              {sync.isPending ? 'Aggiornamento…' : 'Aggiorna'}
            </button>
          </>
        ) : (
          'K-Presenze non è collegato in questo ambiente: le ore si inseriscono a mano.'
        )}
      </p>
      {(link.error || sync.isError) && (
        <p className="notice">{link.error ?? sync.error?.message}</p>
      )}
      {link.unmatched.length > 0 && (
        <p className="notice">
          Ore di K-Presenze di persone che in Persone non hanno la stessa e-mail:{' '}
          {link.unmatched.join(', ')}. Correggi l’e-mail aziendale (o, per i collaboratori, quella
          personale) in Persone.
        </p>
      )}
      {data.readOnly && (
        <p className="notice info">
          {job.kind === 'progetto'
            ? 'Il progetto ha sottoprogetti: qui la somma delle loro persone e ore, in sola lettura.'
            : 'La commessa ha sottocommesse: qui la somma delle loro persone e ore, in sola lettura.'}
        </p>
      )}
      <section className="card stack">
        <div className="row between">
          <h3>
            Persone <span className="count-pill">{data.people.length}</span>
          </h3>
          {editable && (
            <button type="button" onClick={() => setPlanning(true)}>
              Persone e ore previste…
            </button>
          )}
        </div>
        {data.people.length === 0 ? (
          <p className="muted">
            Nessuna persona: indica chi lavora{' '}
            {job.kind === 'progetto' ? 'al progetto' : 'alla commessa'} e le ore previste.
          </p>
        ) : (
          <div className="table-scroll">
            <table className="lines">
              <thead>
                <tr>
                  <th>Persona</th>
                  <th>Ruolo</th>
                  <th>Carica le ore in K-Presenze</th>
                  <th className="right">Ore previste</th>
                  <th className="right">Ore lavorate</th>
                  {costs && (
                    <>
                      <th className="right">€/ora</th>
                      <th className="right">Costo previsto</th>
                      <th className="right">Costo consuntivo</th>
                    </>
                  )}
                </tr>
              </thead>
              <tbody>
                {data.people.map((person) => (
                  <tr key={person.employeeId}>
                    <td>
                      <strong>{person.name}</strong>{' '}
                      <small className="muted">{person.relationship}</small>
                    </td>
                    <td>{person.role ?? '—'}</td>
                    <td>
                      {person.loadsHours === 'sempre' ? (
                        <span className="pill muted">Sempre (dipendente)</span>
                      ) : person.loadsHours === 'abilitato' ? (
                        <span className="pill ok">Abilitato</span>
                      ) : (
                        <span className="pill muted">Non abilitato</span>
                      )}
                      {person.missingEmail && (
                        <>
                          {' '}
                          <span
                            className="pill warn"
                            title="Senza e-mail in Persone le sue ore di K-Presenze non si collegano."
                          >
                            senza e-mail
                          </span>
                        </>
                      )}
                    </td>
                    <td className="right">
                      {person.plannedHours ? formatHours(person.plannedHours) : '—'}
                    </td>
                    <td className="right">{formatHours(person.workedHours)}</td>
                    {costs && (
                      <>
                        <td className="right">
                          {person.hourly ? (
                            formatMoney(person.hourly)
                          ) : (
                            <span className="muted" title="Senza paghe caricate o senza accordo">
                              da definire
                            </span>
                          )}
                        </td>
                        <td className="right">{money(person.plannedCost)}</td>
                        <td className="right">{money(person.actualCost)}</td>
                      </>
                    )}
                  </tr>
                ))}
                <tr>
                  <td colSpan={3} className="muted">
                    Totale
                  </td>
                  <td className="right">
                    <strong>{formatHours(data.totals.plannedHours)}</strong>
                  </td>
                  <td className="right">
                    <strong>{formatHours(data.totals.workedHours)}</strong>
                  </td>
                  {costs && (
                    <>
                      <td />
                      <td className="right">
                        <strong>{money(data.totals.plannedCost)}</strong>
                      </td>
                      <td className="right">
                        <strong>{money(data.totals.actualCost)}</strong>
                      </td>
                    </>
                  )}
                </tr>
              </tbody>
            </table>
          </div>
        )}
        <p className="muted small">
          I dipendenti caricano le ore su ogni commessa e progetto in corso; un collaboratore solo
          dove è tra le Persone: aggiungerlo lo abilita, toglierlo lo disabilita (le ore già
          caricate restano).
        </p>
        <p className="muted small">
          {costs
            ? 'Il costo è ore × €/ora: per i dipendenti l’€/ora dell’anno dalle paghe caricate (o dell’anno prima), per i collaboratori l’accordo in vigore in quel giorno.'
            : 'Gli importi li vede solo chi ha la funzione «Costi delle persone»: qui le ore.'}
        </p>
      </section>

      <section className="card stack">
        <div className="row between">
          <h3>
            Ore lavorate <span className="count-pill">{data.entries.length}</span>
          </h3>
          {editable && (
            <button type="button" className="primary" onClick={() => setAdding(true)}>
              Ore a mano…
            </button>
          )}
        </div>
        {data.entries.length === 0 ? (
          <p className="muted">
            Nessuna ora. Finché K-Presenze non è collegato si inseriscono a mano, per un giorno o
            come totale di un periodo.
          </p>
        ) : (
          <div className="table-scroll">
            <table className="lines">
              <thead>
                <tr>
                  <th>Periodo</th>
                  <th>Persona</th>
                  {data.readOnly && (
                    <th>{job.kind === 'progetto' ? 'Sottoprogetto' : 'Sottocommessa'}</th>
                  )}
                  <th className="right">Ore</th>
                  {costs && <th className="right">Costo</th>}
                  <th>Da dove</th>
                  <th>Nota</th>
                  {editable && <th />}
                </tr>
              </thead>
              <tbody>
                {data.entries.map((entry) => (
                  <tr key={entry.id}>
                    <td>
                      {entry.fromOn === entry.toOn
                        ? formatDate(entry.fromOn)
                        : `${formatDate(entry.fromOn)} – ${formatDate(entry.toOn)}`}
                      {entry.overlaps && (
                        <>
                          {' '}
                          <span
                            className="pill warn"
                            title="Un’altra riga della stessa persona copre gli stessi giorni: controlla di non contare due volte."
                          >
                            si sovrappone
                          </span>
                        </>
                      )}
                    </td>
                    <td>{entry.name}</td>
                    {data.readOnly && <td>{entry.jobCode}</td>}
                    <td className="right">{formatHours(entry.hours)}</td>
                    {costs && <td className="right">{money(entry.cost)}</td>}
                    <td>
                      <span className={`pill ${entry.source === 'k-presenze' ? 'ok' : 'muted'}`}>
                        {HOURS_SOURCE_LABELS[entry.source]}
                      </span>
                      {entry.provisional && (
                        <>
                          {' '}
                          <span
                            className="pill warn"
                            title="Il mese in K-Presenze non è ancora finalizzato."
                          >
                            provvisorio
                          </span>
                        </>
                      )}
                      {entry.createdBy && <small className="muted"> da {entry.createdBy}</small>}
                    </td>
                    <td className="muted">{entry.note ?? ''}</td>
                    {editable && (
                      <td className="right">
                        {entry.source === 'manuale' && (
                          <button type="button" className="link" onClick={() => setRemoving(entry)}>
                            Togli
                          </button>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <p className="muted small">
          Le ore da K-Presenze arrivano da sole, sono provvisorie finché il mese non è finalizzato e
          si correggono in K-Presenze; quelle a mano restano possibili, con la nota e chi le ha
          inserite.
        </p>
      </section>
      {planning && <ResourcesDialog job={job} data={data} onClose={() => setPlanning(false)} />}
      {adding && <HoursDialog job={job} data={data} onClose={() => setAdding(false)} />}
      {removing && (
        <ConfirmDialog
          title={`Togliere ${formatHours(removing.hours)} ore di ${removing.name}?`}
          confirmLabel="Togli"
          busy={remove.isPending}
          error={remove.isError ? remove.error.message : null}
          onConfirm={() => remove.mutate(removing)}
          onCancel={() => setRemoving(null)}
        >
          <p>
            Escono dal consuntivo della {noun === 'progetto' ? 'scheda del progetto' : 'commessa'};
            resta la traccia nel registro attività.
          </p>
        </ConfirmDialog>
      )}
    </section>
  );
}

function useChoices() {
  return useQuery({
    queryKey: [...jobKeys.all, 'people-choices'],
    queryFn: () => apiRequest<JobPersonChoice[]>('/jobs/people-choices'),
  });
}

/** Who works on it, with role and planned hours: the whole list at once. */
function ResourcesDialog({
  job,
  data,
  onClose,
}: {
  job: JobResponse;
  data: JobHoursResponse;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const dialogRef = useRef<HTMLDialogElement>(null);
  const choices = useChoices();
  const [rows, setRows] = useState(
    data.people
      .filter((person) => person.plannedHours !== null || person.role !== null)
      .map((person) => ({
        employeeId: person.employeeId,
        role: person.role ?? '',
        plannedHours: person.plannedHours ? formatHours(person.plannedHours) : '',
      })),
  );
  const [error, setError] = useState('');
  useEffect(() => {
    dialogRef.current?.showModal();
  }, []);
  const save = useMutation({
    mutationFn: (body: JobResourcesRequest) =>
      apiRequest<JobHoursResponse>(`/jobs/${job.id}/resources`, { method: 'PUT', body }),
    onError: (failure) => setError(failure.message),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: hoursKey(job.id) });
      onClose();
    },
  });
  const update = (index: number, change: Partial<(typeof rows)[number]>) =>
    setRows(rows.map((row, at) => (at === index ? { ...row, ...change } : row)));
  const chosen = new Set(rows.map((row) => row.employeeId));
  return (
    <dialog ref={dialogRef} className="dialog wide-dialog" onClose={onClose}>
      <div className="stack">
        <h2>Persone e ore previste · {job.code}</h2>
        <p className="muted small">
          Dipendenti e collaboratori di Persone: un collaboratore nell’elenco è abilitato a caricare
          le ore in K-Presenze. Le ore previste, per l’€/ora, fanno il costo previsto delle persone.
        </p>
        {rows.map((row, index) => (
          <div key={index} className="row">
            <select
              style={{ flex: 2, width: 'auto' }}
              value={row.employeeId}
              onChange={(event) => update(index, { employeeId: event.target.value })}
              aria-label="Persona"
            >
              <option value="">Scegli…</option>
              {choices.data
                ?.filter((item) => item.id === row.employeeId || !chosen.has(item.id))
                .map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.name} ({item.relationship})
                  </option>
                ))}
            </select>
            <input
              style={{ flex: 1.5, width: 'auto' }}
              placeholder="Ruolo"
              value={row.role}
              onChange={(event) => update(index, { role: event.target.value })}
              aria-label="Ruolo"
            />
            <input
              style={{ flex: 1, width: 'auto' }}
              inputMode="decimal"
              placeholder="Ore previste"
              value={row.plannedHours}
              onChange={(event) => update(index, { plannedHours: event.target.value })}
              aria-label="Ore previste"
            />
            <button
              type="button"
              className="link"
              onClick={() => setRows(rows.filter((_, at) => at !== index))}
            >
              Togli
            </button>
          </div>
        ))}
        <button
          type="button"
          className="link"
          onClick={() => setRows([...rows, { employeeId: '', role: '', plannedHours: '' }])}
        >
          + Aggiungi persona
        </button>
        {error && <p className="error">{error}</p>}
        <div className="actions">
          <button type="button" onClick={onClose} disabled={save.isPending}>
            Annulla
          </button>
          <button
            type="button"
            className="primary"
            disabled={save.isPending || rows.some((row) => !row.employeeId)}
            onClick={() => {
              const body: JobResourcesRequest = {
                people: rows.map((row) => ({
                  employeeId: row.employeeId,
                  role: row.role,
                  plannedHours: row.plannedHours.trim() || null,
                })),
              };
              const checked = validate(jobResourcesSchema, body);
              if (Object.keys(checked.errors).length) {
                setError('Controlla le ore previste: un numero maggiore di zero.');
                return;
              }
              save.mutate(body);
            }}
          >
            {save.isPending ? 'Attendere…' : 'Salva'}
          </button>
        </div>
      </div>
    </dialog>
  );
}

/** Hours by hand: a day, or the total of a period, with a note. */
function HoursDialog({
  job,
  data,
  onClose,
}: {
  job: JobResponse;
  data: JobHoursResponse;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const dialogRef = useRef<HTMLDialogElement>(null);
  const choices = useChoices();
  const today = new Date().toISOString().slice(0, 10);
  const [period, setPeriod] = useState(false);
  const [form, setForm] = useState({
    employeeId: data.people[0]?.employeeId ?? '',
    fromOn: today,
    toOn: today,
    hours: '',
    note: '',
  });
  const [errors, setErrors] = useState<FieldErrors>({});
  const [formError, setFormError] = useState('');
  useEffect(() => {
    dialogRef.current?.showModal();
  }, []);
  const text = (key: keyof typeof form) => ({
    value: form[key],
    onChange: (event: { target: { value: string } }) =>
      setForm({ ...form, [key]: event.target.value }),
  });
  const save = useMutation({
    mutationFn: (body: JobHoursRequest) =>
      apiRequest<JobHoursResponse>(`/jobs/${job.id}/hours`, { method: 'POST', body }),
    onError: (error) => showServerError(error, setErrors, setFormError),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: hoursKey(job.id) });
      onClose();
    },
  });
  // The people of the job first, then everyone else in force.
  const own = new Set(data.people.map((person) => person.employeeId));
  const options = [...(choices.data ?? [])].sort(
    (a, b) => Number(own.has(b.id)) - Number(own.has(a.id)),
  );
  return (
    <dialog ref={dialogRef} className="dialog" onClose={onClose}>
      <div className="stack">
        <h2>Ore a mano · {job.code}</h2>
        <Field label="Persona" error={errors.employeeId}>
          <select {...text('employeeId')}>
            <option value="">Scegli…</option>
            {options.map((item) => (
              <option key={item.id} value={item.id}>
                {item.name}
                {own.has(item.id) ? '' : ' (non tra le persone previste)'}
              </option>
            ))}
          </select>
        </Field>
        <div className="segmented-small" role="radiogroup" aria-label="Giorno o periodo">
          <button
            type="button"
            className={period ? '' : 'selected'}
            onClick={() => {
              setPeriod(false);
              setForm({ ...form, toOn: form.fromOn });
            }}
          >
            Un giorno
          </button>
          <button
            type="button"
            className={period ? 'selected' : ''}
            onClick={() => setPeriod(true)}
          >
            Totale di un periodo
          </button>
        </div>
        <div className="form-grid">
          <Field label={period ? 'Dal' : 'Giorno'} error={errors.fromOn}>
            <input
              type="date"
              value={form.fromOn}
              onChange={(event) =>
                setForm({
                  ...form,
                  fromOn: event.target.value,
                  toOn: period ? form.toOn : event.target.value,
                })
              }
            />
          </Field>
          {period && (
            <Field label="Al" error={errors.toOn}>
              <input type="date" {...text('toOn')} />
            </Field>
          )}
          <Field label="Ore" error={errors.hours}>
            <input inputMode="decimal" autoFocus {...text('hours')} />
          </Field>
        </div>
        <Field label="Nota" error={errors.note}>
          <input
            {...text('note')}
            maxLength={300}
            placeholder={period ? 'totale di settembre' : ''}
          />
        </Field>
        {formError && <p className="error">{formError}</p>}
        <div className="actions">
          <button type="button" onClick={onClose} disabled={save.isPending}>
            Annulla
          </button>
          <button
            type="button"
            className="primary"
            disabled={save.isPending || !form.employeeId || !form.hours.trim()}
            onClick={() => {
              const body: JobHoursRequest = { ...form, toOn: period ? form.toOn : form.fromOn };
              const checked = validate(jobHoursSchema, body);
              setErrors(checked.errors);
              setFormError(
                Object.keys(checked.errors).length ? 'Correggi i campi segnati in rosso.' : '',
              );
              if (!Object.keys(checked.errors).length) save.mutate(body);
            }}
          >
            {save.isPending ? 'Attendere…' : 'Salva'}
          </button>
        </div>
      </div>
    </dialog>
  );
}
